import { Button, Dialog, Field, Input, Switch } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ActionCreate, ActionsService, type ApiError } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface AddActionProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddAction = ({ isOpen, onClose }: AddActionProps) => {
  const { slug } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showToast = useCustomToast();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ActionCreate>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      priority: false,
      archived: false,
      order: 0,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: ActionCreate) =>
      ActionsService.createAction({
        companySlug: slug,
        requestBody: data,
      }),
    onSuccess: () => {
      showToast('Success!', 'Action created successfully.', 'success');
      reset();
      onClose();
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const onSubmit: SubmitHandler<ActionCreate> = (data) => {
    mutation.mutate(data);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()} placement="center">
      <Dialog.Backdrop />
      <Dialog.Content as="form" onSubmit={handleSubmit(onSubmit)}>
        <Dialog.CloseTrigger />
        <Dialog.Header>
          <Dialog.Title>{t('action.drawer.add.title')}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Field.Root required invalid={!!errors.name}>
            <Field.Label>
              {t('action.columnsHeader.name')}
              <Field.RequiredIndicator />
            </Field.Label>
            <Input
              id="name"
              {...register('name', {
                required: t('action.columnsHeader.name.required'),
              })}
              placeholder={t('action.columnsHeader.name.placeholder')}
              type="text"
            />
            <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root mt={4}>
            <Switch.Root
              name={'archived'}
              defaultChecked={getValues().archived}
              onCheckedChange={(e) => setValue('archived', e.checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label>{t('action.columnsHeader.archived')}</Switch.Label>
            </Switch.Root>
          </Field.Root>

          <Field.Root mt={4}>
            <Switch.Root
              name={'priority'}
              defaultChecked={getValues().priority}
              onCheckedChange={(e) => setValue('priority', e.checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label>{t('action.columnsHeader.priority')}</Switch.Label>
            </Switch.Root>
          </Field.Root>

          <Field.Root required invalid={!!errors.order} mt={4}>
            <Field.Label>
              {t('action.columnsHeader.order')}
              <Field.RequiredIndicator />
            </Field.Label>
            <Input
              id="order"
              {...register('order', {
                required: t('action.columnsHeader.order.required'),
              })}
              placeholder={t('action.columnsHeader.order.placeholder')}
              type="number"
            />
            <Field.ErrorText>{errors.order?.message}</Field.ErrorText>
          </Field.Root>
        </Dialog.Body>
        <Dialog.Footer>
          <Button onClick={onClose} variant="outline">
            {t('ui.cancel')}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {t('ui.save')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddAction;
